import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const UsersContext = createContext(null);
const API_URL = import.meta.env.VITE_API_URL || "https://api.endoac.ro";

const normalizeUser = (raw = {}) => ({
  id: raw.id,
  name: raw.name || "",
  email: raw.email || "",
  role: raw.role || "patient",
  phone: raw.phone || "",
  createdAt: raw.createdAt ? new Date(raw.createdAt).toISOString() : null,
  updatedAt: raw.updatedAt ? new Date(raw.updatedAt).toISOString() : null,
});

const sortUsersByNewest = (list = []) =>
  [...list].sort((a, b) => {
    const aTime = a.createdAt ? Date.parse(a.createdAt) : 0;
    const bTime = b.createdAt ? Date.parse(b.createdAt) : 0;
    return bTime - aTime;
  });

export function UsersProvider({ children }) {
  const { token, currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const authHeaders = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const fetchUsers = useCallback(async () => {
    if (!token) {
      setUsers([]);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        headers: {
          ...authHeaders,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Nu s-au putut încărca utilizatorii");
      }
      const list = data?.data?.users || [];
      setUsers(sortUsersByNewest(list.map(normalizeUser)));
    } catch (err) {
      console.error("Eroare fetch utilizatori:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, token]);

  useEffect(() => {
    if (currentUser?.role === "admin") {
      fetchUsers();
    } else {
      setUsers(currentUser ? [normalizeUser(currentUser)] : []);
    }
  }, [currentUser, fetchUsers]);

  const addUser = useCallback(
    async (payload) => {
      if (!token) return null;
      try {
        const res = await fetch(`${API_URL}/api/users`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut crea utilizatorul");
        }
        const user = normalizeUser(data?.data?.user);
        setUsers((prev) => sortUsersByNewest([user, ...prev]));
        return user;
      } catch (err) {
        console.error("Eroare creare utilizator:", err);
        throw err;
      }
    },
    [authHeaders, token]
  );

  const updateUser = useCallback(
    async (payload) => {
      if (!payload?.id || !token) return null;
      try {
        const res = await fetch(`${API_URL}/api/users/${payload.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut actualiza utilizatorul");
        }
        const user = normalizeUser(data?.data?.user);
        setUsers((prev) => sortUsersByNewest(prev.map((u) => (u.id === user.id ? user : u))));
        return user;
      } catch (err) {
        console.error("Eroare actualizare utilizator:", err);
        throw err;
      }
    },
    [authHeaders, token]
  );

  const deleteUser = useCallback(
    async (userId) => {
      if (!userId || !token) return false;
      try {
        const res = await fetch(`${API_URL}/api/users/${userId}`, {
          method: "DELETE",
          headers: {
            ...authHeaders,
          },
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut șterge utilizatorul");
        }
        setUsers((prev) => prev.filter((u) => u.id !== userId));
        return true;
      } catch (err) {
        console.error("Eroare ștergere utilizator:", err);
        throw err;
      }
    },
    [authHeaders, token]
  );

  const promoteUser = useCallback(
    async (userId, role) => {
      if (!userId || !role || !token) return null;
      try {
        const res = await fetch(`${API_URL}/api/users/${userId}/promote`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
          body: JSON.stringify({ role }),
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Nu s-a putut actualiza rolul utilizatorului");
        }
        const user = normalizeUser(data?.data?.user);
        setUsers((prev) => sortUsersByNewest(prev.map((u) => (u.id === user.id ? user : u))));
        return user;
      } catch (err) {
        console.error("Eroare schimbare rol utilizator:", err);
        throw err;
      }
    },
    [authHeaders, token]
  );

  const value = useMemo(
    () => ({
      users,
      loading,
      error,
      fetchUsers,
      addUser,
      updateUser,
      deleteUser,
      promoteUser,
    }),
    [users, loading, error, fetchUsers, addUser, updateUser, deleteUser, promoteUser]
  );

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
}

export const useUsers = () => useContext(UsersContext);
